import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../../models/stores/Root';
import AddressList from '../../../components/input/AddressList';
import { AddressContract, AddressFormData } from '../../../types/Address';
import AddNewAddressForm from '../../../components/input/AddNewAddressForm';
import { SelectOption } from '../../../types/common';
import { ICustomerBillingAddressModel } from '../../../models/stores/sub-stores/Customer';
import { CheckoutEvent } from '../../../models/machines/checkout-machine';

export type EditBillToViewProps = {
  onAddressChanged: (
    address: ICustomerBillingAddressModel,
    eventType: CheckoutEvent
  ) => void;
};

const EditBillToView: React.FC<EditBillToViewProps> = observer(
  ({ onAddressChanged }: EditBillToViewProps) => {
    const rootStore = useMst();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { configuration, customer, cart, currentStep } = rootStore;
    const [hasAddressErrors, setHasAddressErrors] = useState(false);
    const [isSavingAddress, setIsSavingAddress] = useState(false);

    const countryOptions = configuration.countriesAsSelectOptions;

    const handleGetStateOptions = (
      country: string
    ): { options: SelectOption[]; label: string } => {
      const options = configuration.getCurrentStatesAsSelectOptions(country);
      const label = configuration.getStateLabel(country);
      return { options, label };
    };

    const handleAddBillingAddress = (): void => {
      rootStore.send({ type: 'ADD_BILLING_ADDRESS' });
    };

    const handleCancelClick = (): void => {
      rootStore.send({ type: 'EDIT_BILLING_CANCELED' });
    };

    const handleBillingAddressSelected = (selectedAddressId: number): void => {
      const selectedAddress = customer.getBillingAddressById(selectedAddressId);
      cart.updateBillingAddress(selectedAddress as AddressContract);
      onAddressChanged(selectedAddress as ICustomerBillingAddressModel, {
        type: 'ADDRESS_SELECTED',
      });
    };

    const handleAddNewBillingAddress = async (
      formData: AddressFormData
    ): Promise<void> => {
      setIsSavingAddress(true);
      const createdAddress =
        await customer.addBillingAddressToAuthenticatedCustomer({
          ...formData,
          id: null,
          preferred: true,
        } as AddressContract);
      cart.updateCustomer({ phoneNumber: formData.phoneNumber });
      cart.updateBillingAddress(createdAddress);
      onAddressChanged(createdAddress, { type: 'ADDRESS_ADDED' });
      setIsSavingAddress(false);
    };

    const handleAddAddressCanceled = (): void => {
      rootStore.send({ type: 'ADD_ADDRESS_CANCEL' });
    };

    if (rootStore.stateMatches('OneClick.BillingAddress.List')) {
      return (
        <AddressList
          title="Bill To"
          subTitle="Choose a billing address. Should match your payment method. (i.e. billing address on file for your credit card)"
          addresses={customer.billingAddresses}
          addActionText="Or add a new address"
          onAddActionClicked={handleAddBillingAddress}
          onAddressSelected={handleBillingAddressSelected}
          onCancel={handleCancelClick}
          selectedAddressId={cart.billingAddress?.id}
        />
      );
    }
    if (rootStore.stateMatches('OneClick.BillingAddress.Add')) {
      return (
        <AddNewAddressForm
          title="Add Billing Address"
          isShippingAddress={false}
          onCancel={handleAddAddressCanceled}
          countryOptions={countryOptions}
          getStateOptions={handleGetStateOptions}
          onErrorStatus={setHasAddressErrors}
          hasAddressErrors={hasAddressErrors}
          defaultCountry={configuration.defaultCountry}
          onAddressSaved={handleAddNewBillingAddress}
          showProgress={isSavingAddress}
        />
      );
    }

    return null;
  }
);

export default EditBillToView;
